import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useIndustry } from '../lib/IndustryContext';
import { useLocale } from '../lib/LocaleContext';
import styles from './ChatInterface.module.css';

export default function ChatInterface({ hideHeader = false, newSessionTriggerRef }) {
  const { industry, industryInfo } = useIndustry();
  const { t } = useLocale();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sseEventSourceRef = useRef(null);

  const [customerId, setCustomerId] = useState(() => {
    if (typeof window !== 'undefined') {
      let stored = localStorage.getItem('customerId');
      if (stored?.startsWith('customer-')) {
        stored = `sg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('customerId', stored);
      }
      if (!stored) {
        stored = `sg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('customerId', stored);
      }
      return stored;
    }
    return `sg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  });

  const [sessionId, setSessionId] = useState(() => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const startNewSession = useCallback(() => {
    setMessages([]);
    setIsLoading(false);
    setStreamingText('');
    if (sseEventSourceRef.current) {
      sseEventSourceRef.current.close();
      sseEventSourceRef.current = null;
    }
    setSessionId(`${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  // 向父组件暴露 startNewSession，使父组件可触发新对话
  useEffect(() => {
    if (newSessionTriggerRef) newSessionTriggerRef.current = startNewSession;
  }, [newSessionTriggerRef, startNewSession]);

  // 首次访问新行业页面时清空历史，避免不同行业间的对话混淆
  const prevIndustryRef = useRef(industry);
  useEffect(() => {
    if (prevIndustryRef.current !== industry) {
      if (prevIndustryRef.current !== undefined && prevIndustryRef.current !== '') {
        startNewSession();
      }
      prevIndustryRef.current = industry;
    }
  }, [industry, startNewSession]);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => () => {
    if (sseEventSourceRef.current) sseEventSourceRef.current.close();
  }, []);

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;
    const messageId = generateId();
    const userMessage = { id: generateId(), text: inputText.trim(), sender: 'user', timestamp: Date.now(), messageId };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputText.trim();
    setInputText('');
    setIsLoading(true);
    setStreamingText('');

    try {
      // 发送时从 URL 读取 industry，确保与当前页面一致（避免 state 滞后）
      const industryParam = typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('industry') || 'ev')
        : (industry || 'ev');
      const res = await api.getChatMessage({
        message: currentInput,
        messageId,
        customerId,
        sessionId,
        timestamp: userMessage.timestamp,
        industry: industryParam
      });
      const isOk = res.Status === 1;
      const sessionIdFromApi = res.Entity ?? res.data ?? res;
      if (!isOk || !sessionIdFromApi) throw new Error(res.Message || res.message || t.sessionFailed);

      const es = new EventSource(api.sseUrl(typeof sessionIdFromApi === 'string' ? sessionIdFromApi : sessionIdFromApi));
      sseEventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const typ = payload.type;
          if (typ === 'node_response') {
            const reply = payload.data?.ResponseData?.reply;
            if (reply) {
              es.close();
              sseEventSourceRef.current = null;
              setStreamingText('');
              setIsLoading(false);
              setMessages(prev => [...prev, { id: generateId(), text: reply, sender: 'ai', timestamp: Date.now(), messageId }]);
              window.dispatchEvent(new CustomEvent('aiResponseReceived', { detail: { messageId, customerId } }));
            }
          } else if (typ === 'workflow_completed') {
            const reply = payload.data?.message;
            if (reply && isLoading) {
              es.close();
              sseEventSourceRef.current = null;
              setStreamingText('');
              setIsLoading(false);
              setMessages(prev => [...prev, { id: generateId(), text: reply, sender: 'ai', timestamp: Date.now(), messageId }]);
              window.dispatchEvent(new CustomEvent('aiResponseReceived', { detail: { messageId, customerId } }));
            }
          } else if (typ === 'error') {
            es.close();
            sseEventSourceRef.current = null;
            setStreamingText('');
            setIsLoading(false);
            setMessages(prev => [...prev, { id: generateId(), text: payload.message || t.processError, sender: 'ai', timestamp: Date.now() }]);
          }
        } catch (e) { console.error('Parse SSE error', e); }
      };

      es.onerror = () => {
        es.close();
        sseEventSourceRef.current = null;
        if (isLoading) {
          setIsLoading(false);
          setMessages(prev => [...prev, { id: generateId(), text: t.connectionInterrupted, sender: 'ai', timestamp: Date.now() }]);
        }
      };
    } catch (err) {
      console.error('Chat flow error', err);
      setIsLoading(false);
      setMessages(prev => [...prev, { id: generateId(), text: err.message || t.connectionFailed, sender: 'ai', timestamp: Date.now() }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div className={styles.chatContainer}>
      {!hideHeader && (
        <div className={styles.chatHeader}>
          <div className={styles.iconContainer}>
            <svg className={styles.swimmingIcon} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="45" fill="#4FC3F7" stroke="#0277BD" strokeWidth="3"/>
              <circle cx="50" cy="50" r="35" fill="none" stroke="#0277BD" strokeWidth="2" strokeDasharray="8,8"/>
            </svg>
          </div>
          <h1>{industryInfo?.title || t.customerServiceCenter}</h1>
          {messages.length > 0 && (
            <button onClick={startNewSession} className={styles.newSessionButton} title={t.newConversationTitle}>{t.newConversation}</button>
          )}
        </div>
      )}
      <div className={styles.messagesContainer}>
        {messages.length === 0 && (
          <div className={styles.emptyState}><p>{t.welcomeMessage.replace('{title}', industryInfo?.title || t.customerServiceCenter)}</p></div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`${styles.message} ${m.sender === 'user' ? styles.userMessage : styles.aiMessage}`}>
            <div className={styles.messageBubble}><p>{m.text}</p></div>
          </div>
        ))}
        {isLoading && (
          <div className={`${styles.message} ${styles.aiMessage}`}>
            <div className={styles.messageBubble}>
              {streamingText ? <p className={styles.streamingText}>{streamingText}</p> : (
                <div className={styles.typingIndicator}><span></span><span></span><span></span></div>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className={styles.inputContainer}>
        <div className={styles.inputInner}>
          <textarea ref={inputRef} className={styles.input} value={inputText} onChange={e => setInputText(e.target.value)}
            onKeyPress={handleKeyPress} placeholder={t.inputPlaceholder} rows={1} disabled={isLoading} />
          <button className={styles.sendButton} onClick={handleSend} disabled={!inputText.trim() || isLoading}>{t.send}</button>
        </div>
      </div>
    </div>
  );
}
